import firebase from "firebase/compat";
import { geohashForLocation } from "geofire-common";
import { FC, useRef, useState } from "react";
import {
  Create,
  CreateProps,
  required,
  SelectInput,
  FormGroupContextProvider,
  FormWithRedirect,
  useLoading,
  BooleanInput,
  NumberInput,
  minValue,
} from "react-admin";
import { Field } from "react-final-form";

import CustomCard from "../../components/CustomCard";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Location from "../../components/Location";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { AppResource, Enums } from "../../types/resource.types";
import Spacing from "../../components/Spacing";
import useCurrentUser from "../../hooks/useCurrentUser";
import { FormGroupNames } from "../../types/reactAdmin.types";
import CustomToolbar from "../../components/CustomToolbar";
import { usePlacesLocalizations } from "./utils/usePlaceLocalization";
import useGetEnumsAsChoices from "../../hooks/useGetEnumsAsChoices";
import Aavailabilities from "./components/Aavailabilities";
import LocalizedRichText from "./components/LocalizedRichText";
import Videos from "./components/Videos";

const CustomForm: FC<any> = (props) => {
  const loading = useLoading();
  const localizedInputs = usePlacesLocalizations(props);
  const { availabilitiesRefs, servicesRefs, contactUsRefs, videosRefs } = props;

  const [availabilitiesErrors, setAavailabilitiesErrors] = useState();
  const [servicesHasError, setServicesHasError] = useState(false);
  const [contactUsHasError, setContactUsHasError] = useState(false);
  const [videosError, setVideosError] = useState(false);

  const { disabledStyles } = useDisabledOnLoading(loading);
  const citiesEnums = useGetEnumsAsChoices<Enums>(Enums.cities);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.placesCreate}>
        <FormWithRedirect
          {...props}
          redirect={`/${AppResource.places}`}
          render={(formProps) => {
            return (
              <form style={disabledStyles}>
                <CustomCard title="Details">
                  <Spacing>
                    {localizedInputs?.[0]}
                    {localizedInputs?.[1]}
                  </Spacing>
                  <Spacing>
                    {localizedInputs?.[2]}
                    {localizedInputs?.[3]}
                  </Spacing>
                  <Spacing>
                    <NumberInput source="price" variant="outlined" fullWidth validate={[required(), minValue(0)]} />
                    <BooleanInput source="disabled" defaultValue={false} />
                  </Spacing>
                </CustomCard>

                <CustomCard title="Aavailabilities">
                  <Aavailabilities
                    onChangeDates={(availabilities: any) => (availabilitiesRefs.current = availabilities)}
                    setError={setAavailabilitiesErrors}
                  />
                </CustomCard>

                <Videos onChangeVideos={(videos: any) => (videosRefs.current = videos)} setError={setVideosError} />

                <CustomCard title="Gallery">
                  <ImageUploadInput multiple source="gallery" validate={[required()]} />
                </CustomCard>

                <CustomCard title="Profile image">
                  <ImageUploadInput source="profileImage" validate={[required()]} />
                </CustomCard>

                <CustomCard title="Cover image">
                  <ImageUploadInput source="coverImage" validate={[required()]} />
                </CustomCard>

                <CustomCard title="Services">
                  <LocalizedRichText
                    onChange={(services: any) => (servicesRefs.current = services)}
                    hasError={(error) => {
                      setServicesHasError(error);
                    }}
                    initialValue={undefined}
                  />
                </CustomCard>

                <CustomCard title="Contact Us">
                  <LocalizedRichText
                    onChange={(contactUs: any) => (contactUsRefs.current = contactUs)}
                    hasError={(error) => {
                      setContactUsHasError(error);
                    }}
                    initialValue={undefined}
                  />
                </CustomCard>

                <CustomCard title="Location">
                  <Spacing>
                    {localizedInputs?.[4]}
                    {localizedInputs?.[5]}
                  </Spacing>
                  <SelectInput
                    source="city"
                    choices={citiesEnums}
                    variant="outlined"
                    validate={[required()]}
                    fullWidth
                    label="City"
                  />
                  <Field name="location" validate={required()}>
                    {(fieldProps) => (
                      <Location
                        onLocationChange={(data) => {
                          if (data?.lat && data?.lng) {
                            const geohash = geohashForLocation([data.lat, data.lng]);
                            const geopoint = new firebase.firestore.GeoPoint(data.lat, data.lng);
                            fieldProps.input.onChange({
                              geopoint,
                              geohash,
                            });
                          }
                        }}
                      />
                    )}
                  </Field>
                </CustomCard>

                <CustomToolbar
                  {...formProps}
                  formName={FormGroupNames.placesCreate}
                  customErrors={{
                    availabilities: availabilitiesErrors ? "Availabilities is required" : "",
                    services: servicesHasError ? "Services is required" : "",
                    contactUs: contactUsHasError ? "Contact Us is required" : "",
                    videos: !videosError ? "Videos is required" : "",
                  }}
                />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const PlacesCreate: FC<CreateProps> = (props) => {
  const availabilitiesRefs = useRef<any[]>([]);
  const servicesRefs = useRef<{}>();
  const contactUsRefs = useRef<{}>();
  const videosRefs = useRef<[]>();
  const { user } = useCurrentUser();

  return (
    <Create
      {...props}
      transform={(values: any) => {
        values.ownerId = user.id;
        values.ownerType = user.role;
        values.availabilities = availabilitiesRefs.current;
        values.services = servicesRefs.current;
        values.contact = contactUsRefs.current;
        values.videos = videosRefs.current;
        values.address = {
          city: values.city,
          country: { en: "qatar", ar: "قطر" },
          location: values.location,
          details: values.details,
        };
        const { city, country, location, details, ...newValues } = values;

        return newValues;
      }}
      component="section"
    >
      <CustomForm
        availabilitiesRefs={availabilitiesRefs}
        servicesRefs={servicesRefs}
        contactUsRefs={contactUsRefs}
        videosRefs={videosRefs}
      />
    </Create>
  );
};

export default PlacesCreate;
